import TagManager from 'react-gtm-module';

import { getConfig } from 'src/global-config';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

// GTM and the GA4 tag it loads cost about a second of main-thread work, so the script waits for the
// first interaction, or a few seconds after the page has loaded, instead of competing with startup.
const GTM_FALLBACK_DELAY_MS = 5000;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;

let gtmId = '';
let gtmScriptAdded = false;

// ----------------------------------------------------------------------

export function initializeGTM(): void {
  if (gtmId) {
    return;
  }

  // Nothing to do without a GTM ID
  const id = getConfig().googleTagManager.id.trim();
  if (!id) {
    return;
  }

  gtmId = id;

  // The queue exists from the start, so events pushed before the script arrives keep their order.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  INTERACTION_EVENTS.forEach((type) =>
    window.addEventListener(type, loadGTMScript, { once: true, passive: true, capture: true })
  );

  const startFallbackTimer = () => window.setTimeout(loadGTMScript, GTM_FALLBACK_DELAY_MS);
  if (document.readyState === 'complete') {
    startFallbackTimer();
  } else {
    window.addEventListener('load', startFallbackTimer, { once: true });
  }
}

function loadGTMScript(): void {
  if (!gtmId || gtmScriptAdded) {
    return;
  }
  gtmScriptAdded = true;

  INTERACTION_EVENTS.forEach((type) =>
    window.removeEventListener(type, loadGTMScript, { capture: true })
  );

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  document.head.appendChild(script);
}

// ----------------------------------------------------------------------

//Push to GTM via react-gtm-module.
export function pushDataLayer(entry: Record<string, unknown>): void {
  if (typeof window === 'undefined') {
    return;
  }
  TagManager.dataLayer({ dataLayer: entry });
}

//Custom GA4-compatible event
export function trackEvent(eventName: string, payload: Record<string, unknown> = {}): void {
  pushDataLayer({
    event: eventName,
    ...payload,
  });
}

/**
 * For events sent right before leaving the page (e.g. a redirect to Auth0): loads GTM now if it
 * hasn't loaded yet, and resolves once GTM has handled the event or after `timeoutMs`, whichever
 * comes first. Without this, an event queued before GTM loads is lost when the page unloads.
 */
export function trackEventBeforeNavigation(
  eventName: string,
  payload: Record<string, unknown> = {},
  timeoutMs = 2000
): Promise<void> {
  if (!gtmId) {
    trackEvent(eventName, payload);
    return Promise.resolve();
  }

  loadGTMScript();

  return new Promise((resolve) => {
    window.setTimeout(resolve, timeoutMs);
    pushDataLayer({
      event: eventName,
      ...payload,
      eventCallback: () => resolve(),
      eventTimeout: timeoutMs,
    });
  });
}


 //Virtual page view for SPA route changes. Uses event name `spa_page_view`
export function trackPageView(path?: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const page_path = path ?? `${window.location.pathname}${window.location.search}`;

  pushDataLayer({
    event: 'spa_page_view',
    page_path,
    page_location: window.location.href,
    page_title: typeof document !== 'undefined' ? document.title : '',
    app_mode: getConfig().appMode,
  });
}
